// Matrix / match-the-following questions keep their two lists as a JSON array of
// { qlist1, qlist2 } in the question column.
//
// That payload reaches us in more than one shape, depending on which editor
// wrote it:
//   1. plain JSON                      -> [{"qlist1":"<p>..."}]
//   2. the WHOLE array percent-encoded -> %5B%7B%22qlist1%22%3A%22%3Cp%3E...
//   3. plain JSON whose qlist VALUES are percent-encoded
//
// Only shape 1 used to work. On shape 2 JSON.parse threw at position 0 ("%"),
// every caller's catch returned false, and the question rendered as NOTHING —
// no list, no error, just a blank where the match-the-following should be. On
// shape 3 it parsed but printed raw "%3Cp%3E" text. Both are handled here.

// A percent-escape looks like %XX. Question text legitimately contains a bare
// "%" (percentages), and decodeURIComponent throws URIError on a malformed
// sequence, so we only attempt a decode when the string really looks encoded,
// and never let a failure escape.
const PERCENT_ESCAPE = /%[0-9A-Fa-f]{2}/;

export const decodeMaybe = (value) => {
    if (typeof value !== "string" || !PERCENT_ESCAPE.test(value)) return value;
    try {
        return decodeURIComponent(value);
    } catch (e) {
        return value;
    }
};

// Parse the question column into [{ qlist1, qlist2 }] with both lists decoded and
// ready for html-react-parser.
//
// `mangled` is the caller's quote-normalised variant of the same string. It stays
// in the chain as a last resort so payloads that only ever parsed that way keep
// working. Throws when nothing parses, which is what the callers' existing
// try/catch already expects.
export const parseMatrixQuestion = (raw, mangled) => {
    const attempts = [raw, decodeMaybe(raw), mangled, decodeMaybe(mangled)];
    let parsed = null;
    let lastError = null;

    for (let i = 0; i < attempts.length; i++) {
        const candidate = attempts[i];
        if (typeof candidate !== "string" || candidate === "") continue;
        try {
            parsed = JSON.parse(candidate);
            break;
        } catch (e) {
            lastError = e;
        }
    }

    if (!Array.isArray(parsed)) {
        throw lastError || new SyntaxError("matrix question is not a JSON array");
    }

    return parsed.map((row) => (
        row && typeof row === "object"
            ? { ...row, qlist1: decodeMaybe(row.qlist1), qlist2: decodeMaybe(row.qlist2) }
            : row
    ));
};
